import React, {useCallback, useMemo, useState} from 'react';
import {AccessTime} from '@mui/icons-material';
import {Column, Page, Row} from '../../../componentsLib/Layout';
import {BodyText, HeadingText} from '../../../componentsLib/Text';
import {registerGlobalStyle} from '../../../theme';
import {formatDateLong, fullName} from '../../../utils';
import {useParams} from 'react-router';
import {TabPanel, TabbedContent} from '../../../componentsLib/Basic/TabbedContent';
import Files from '../../../modules/file/Files';
import {SalesDocActivityTab, SalesDocCommentsTab, SalesDocRevisionsTab, SalesDocTasksTab, SalesStoreHostingTab} from '../Tabs';
import {SalesDocActionMenuButton, SalesDocSections, SalesDocStatusBar} from '../Components';
import {useSalesDocViewState} from '../State/useSalesDocViewState';
import {useCompany} from '../../../hooks';
import {PresentationView} from '../Views/PresentationView';
import {SalesDocView} from '../Views/SalesDocView';
import {SalesDoc} from '../Models/SalesDoc';

registerGlobalStyle('.salesdoc-page', (theme) => ({
  position: 'relative',
  flexGrow: 1,
  '.text-heading': {
    span: {color: theme.colors.text.highlight},
    strong: {color: theme.colors.palette.red}
  },
  '.salesdoc-page-header': {
    '.text-body': {
      fontSize: '1rem',
      span: {color: theme.colors.text.highlight},
    },
    '.warning': {color: theme.colors.palette.red,},
    '.time-icon': {margin: theme.spacing(.5, .5, .5, 2)},
    '.salesdoc-owner': {marginTop: theme.spacing(2)},
  },
  '.presentation-view': {
    background: theme.colors.background.white,
    border: `1px solid ${theme.colors.border.light}`,
    margin: theme.spacing(0, 0, 2, 0),
    borderRadius: theme.shape.borderRadius,
    paddingBottom: theme.spacing(4),
    '.product-gallery': {
      justifyContent: 'center',
      gap: theme.spacing(1),
      paddingLeft: 0
    }
  },
  '.tabs': {padding: 0},
  '.hoops-page': {position: 'relative'},
}));

export function SalesDocPage({updateField: _updateField, refetchSalesDocs}) {
  const {salesDocNumber, salesDocId} = useParams();
  const {salesDoc, refetchSalesDoc, updateSalesDocCache} = useSalesDocViewState(salesDocNumber ?? salesDocId);
  const {company} = useCompany(salesDoc?.companyTradingEntityId ?? salesDoc?.customer?.companyTradingEntityId);
  const [selection, setSelection] = useState({section: SalesDocSections.documentHeader});

  const isPresentation = salesDoc?.isPresentation() || salesDoc?.isPresentationTemplate();
  const hideActionButtons = true;
  const isDrawer = true;

  const updateField = useCallback(async (...args) => {
    const res = await _updateField(...args);
    if (res?.salesDoc) {
      updateSalesDocCache(res);
    }
    return res;
  }, [_updateField, updateSalesDocCache]);

  const context = useMemo(() => ({
    company,
    currencySymbol: company.currencySymbol,
    hideActionButtons,
    isDrawer,
    refetchSalesDoc,
    salesDoc,
    salesDocToken: salesDoc?._id,
    selection,
    setSelection,
  }), [company, hideActionButtons, isDrawer, refetchSalesDoc, salesDoc, selection]);

  return (
    <Page testId={'salesdoc-page'} className={'salesdoc-page'} context={context} pad>
      {salesDoc &&
        <Column>
          <Row className={'salesdoc-page-header'}>
            <Column fillWidth>
              <Row>
                <HeadingText x24><span>{salesDoc.docTypeName} #{salesDoc.number}</span> {salesDoc.title}</HeadingText>
              </Row>
              <Row>
                <BodyText><span>{[salesDoc.customer?.name, fullName(salesDoc.contact)].filter(Boolean).join(' | ')} </span></BodyText>
                {salesDoc.deadline &&
                  <>
                    <AccessTime fontSize={'small'} className={'warning time-icon'} />
                    <BodyText className={'warning'}> Deadline {formatDateLong(salesDoc.deadline)}</BodyText>
                  </>
                }
              </Row>
              <BodyText className={'salesdoc-owner'}>Owner: {salesDoc.owner?.fullName}</BodyText>
            </Column>
            <Column fillWidth>
              <Row justifyRight>
                <SalesDocActionMenuButton actionButton salesDoc={salesDoc} updateField={updateField} refetchSalesDocs={refetchSalesDocs} />
              </Row>
            </Column>
          </Row>

          <SalesDocStatusBar salesDoc={salesDoc} updateField={updateField} />

          <TabbedContent>
            <TabPanel label='Sale Detail'>
              {!isPresentation && <SalesDocView />}
              {isPresentation && <PresentationView />}
            </TabPanel>
            <TabPanel label={'Hosting'} visible={salesDoc.documentType === SalesDoc.Type.SALESSTORE}>
              <SalesStoreHostingTab salesStore={salesDoc} updateField={updateField}/>
            </TabPanel>
            <TabPanel label='Files'>
              <Files
                customerId={salesDoc.customer?._id}
                getEntityIds={[salesDoc._id]}
                setEntityIds={[salesDoc._id]}
                isWorkable={true}
                createTaskState={{quoteIds: [salesDoc._id], quote: [salesDoc]}}
                showCustomerBtn={true}
                showUploadFileBtn={true}
              />
            </TabPanel>
            <TabPanel label='Comments'>
              <SalesDocCommentsTab salesDoc={salesDoc}/>
            </TabPanel>
            <TabPanel label='Tasks'>
              <SalesDocTasksTab salesDoc={salesDoc} />
            </TabPanel>
            <TabPanel label='BackTrack'>
              <SalesDocRevisionsTab salesDoc={salesDoc} />
            </TabPanel>
            <TabPanel label='Activity'>
              <SalesDocActivityTab quote={salesDoc} />
            </TabPanel>
          </TabbedContent>
        </Column>
      }
    </Page>
  );
}
